import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Table, Badge, Tabs, Tab, Card } from 'react-bootstrap';
import { getEndpointDetails } from '../../services/api';
import './EndpointDetailsModal.css';

function EndpointDetailsModal({ show, onHide, endpointId, endpointName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => {
    if (show && endpointId) {
      loadDetails();
    }
  }, [show, endpointId, selectedDays]);

  const loadDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEndpointDetails(endpointId, selectedDays);
      setDetails(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Details: ' + (err.message || 'Unbekannter Fehler'));
      console.error('Error loading endpoint details:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusBadge = (isOngoing) => {
    return isOngoing ?
      <Badge bg="danger">Läuft noch</Badge> :
      <Badge bg="secondary">Behoben</Badge>;
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-info-circle me-2"></i>
          Uptime Details: {endpointName}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Lädt...</span>
            </Spinner>
            <p className="mt-3">Lade Daten...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <Alert.Heading>Fehler</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        {!loading && !error && details && (
          <>
            {/* Zeitraum-Auswahl */}
            <div className="mb-4">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${selectedDays === 7 ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setSelectedDays(7)}
                >
                  7 Tage
                </button>
                <button
                  type="button"
                  className={`btn ${selectedDays === 30 ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setSelectedDays(30)}
                >
                  30 Tage
                </button>
                <button
                  type="button"
                  className={`btn ${selectedDays === 90 ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setSelectedDays(90)}
                >
                  90 Tage
                </button>
              </div>
            </div>

            <Tabs defaultActiveKey="overview" className="mb-3">
              {/* Übersicht Tab */}
              <Tab eventKey="overview" title="Übersicht">
                <Card className="mb-3">
                  <Card.Header>
                    <strong>Analysezeitraum</strong>
                  </Card.Header>
                  <Card.Body>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td><strong>Zeitraum:</strong></td>
                          <td>{details.analysis_period.days} Tage</td>
                        </tr>
                        <tr>
                          <td><strong>Von:</strong></td>
                          <td>{formatDate(details.analysis_period.start_date)}</td>
                        </tr>
                        <tr>
                          <td><strong>Bis:</strong></td>
                          <td>{formatDate(details.analysis_period.end_date)}</td>
                        </tr>
                        <tr>
                          <td><strong>Check-Intervall:</strong></td>
                          <td>{details.endpoint.check_interval} Sekunden</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <strong>Gesamtstatistiken</strong>
                  </Card.Header>
                  <Card.Body>
                    <Table bordered hover>
                      <tbody>
                        <tr>
                          <td><strong>Uptime Prozentsatz</strong></td>
                          <td className="text-end">
                            <h4 className="mb-0">
                              <Badge bg={details.overall_statistics.uptime_percentage >= 99 ? 'success' : details.overall_statistics.uptime_percentage >= 95 ? 'warning' : 'danger'}>
                                {details.overall_statistics.uptime_percentage}%
                              </Badge>
                            </h4>
                          </td>
                        </tr>
                        <tr className="table-success">
                          <td><strong>Online Zeit</strong></td>
                          <td className="text-end">
                            {details.overall_statistics.total_online_formatted}
                            <br />
                            <small className="text-muted">
                              ({details.overall_statistics.total_online_seconds.toLocaleString()} Sekunden)
                            </small>
                          </td>
                        </tr>
                        <tr className="table-danger">
                          <td><strong>Offline Zeit</strong></td>
                          <td className="text-end">
                            {details.overall_statistics.total_offline_formatted}
                            <br />
                            <small className="text-muted">
                              ({details.overall_statistics.total_offline_seconds.toLocaleString()} Sekunden)
                            </small>
                          </td>
                        </tr>
                        <tr>
                          <td><strong>Gesamt Checks</strong></td>
                          <td className="text-end">{details.overall_statistics.total_checks.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td><strong>Erfolgreiche Checks</strong></td>
                          <td className="text-end text-success">{details.overall_statistics.successful_checks.toLocaleString()}</td>
                        </tr>
                        <tr>
                          <td><strong>Fehlgeschlagene Checks</strong></td>
                          <td className="text-end text-danger">{details.overall_statistics.failed_checks.toLocaleString()}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <strong>Uptime nach Zeitraum</strong>
                  </Card.Header>
                  <Card.Body>
                    <Table bordered>
                      <thead>
                        <tr>
                          <th>Zeitraum</th>
                          <th className="text-end">Uptime %</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Letzte 7 Tage</td>
                          <td className="text-end">
                            <Badge bg={details.uptime_by_period['7_days'] >= 99 ? 'success' : details.uptime_by_period['7_days'] >= 95 ? 'warning' : 'danger'}>
                              {details.uptime_by_period['7_days']}%
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td>Letzte 30 Tage</td>
                          <td className="text-end">
                            <Badge bg={details.uptime_by_period['30_days'] >= 99 ? 'success' : details.uptime_by_period['30_days'] >= 95 ? 'warning' : 'danger'}>
                              {details.uptime_by_period['30_days']}%
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <td>Letzte 90 Tage</td>
                          <td className="text-end">
                            <Badge bg={details.uptime_by_period['90_days'] >= 99 ? 'success' : details.uptime_by_period['90_days'] >= 95 ? 'warning' : 'danger'}>
                              {details.uptime_by_period['90_days']}%
                            </Badge>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                {details.response_time_statistics.average_ms && (
                  <Card className="mb-3">
                    <Card.Header>
                      <strong>Response-Zeit Statistiken</strong>
                    </Card.Header>
                    <Card.Body>
                      <Table bordered>
                        <tbody>
                          <tr>
                            <td><strong>Durchschnitt</strong></td>
                            <td className="text-end">{details.response_time_statistics.average_ms} ms</td>
                          </tr>
                          <tr>
                            <td><strong>Minimum</strong></td>
                            <td className="text-end">{details.response_time_statistics.min_ms} ms</td>
                          </tr>
                          <tr>
                            <td><strong>Maximum</strong></td>
                            <td className="text-end">{details.response_time_statistics.max_ms} ms</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </Tab>

              {/* Downtime-Perioden Tab */}
              <Tab eventKey="downtime" title={`Downtime-Perioden (${details.downtime_periods.length})`}>
                {details.downtime_periods.length === 0 ? (
                  <Alert variant="success">
                    <i className="bi bi-check-circle me-2"></i>
                    Keine Downtime-Perioden im ausgewählten Zeitraum gefunden! 🎉
                  </Alert>
                ) : (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Start</th>
                        <th>Ende</th>
                        <th>Dauer</th>
                        <th>Checks</th>
                        <th>Status</th>
                        <th>Grund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {details.downtime_periods.map((period, index) => (
                        <tr key={index} className={period.ongoing ? 'table-danger' : ''}>
                          <td>{index + 1}</td>
                          <td>{formatDate(period.start)}</td>
                          <td>{period.ongoing ? 'Läuft noch' : formatDate(period.end)}</td>
                          <td><strong>{period.duration_formatted}</strong></td>
                          <td>{period.checks_count}</td>
                          <td>{getStatusBadge(period.ongoing)}</td>
                          <td>
                            <small className="text-muted">
                              {period.reason || 'Keine Details verfügbar'}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              {/* Berechnung Tab */}
              <Tab eventKey="calculation" title="Berechnung">
                <Card>
                  <Card.Header>
                    <strong>Wie wird die Uptime berechnet?</strong>
                  </Card.Header>
                  <Card.Body>
                    <h5 className="mb-3">Uptime-Prozentsatz</h5>
                    <div className="calculation-box mb-4">
                      <code>{details.calculation_explanation.uptime_calculation}</code>
                    </div>

                    <h5 className="mb-3">Online-Zeit</h5>
                    <div className="calculation-box mb-4">
                      <code>{details.calculation_explanation.time_calculation}</code>
                    </div>

                    <h5 className="mb-3">Offline-Zeit</h5>
                    <div className="calculation-box mb-4">
                      <code>{details.calculation_explanation.downtime_calculation}</code>
                    </div>

                    <Alert variant="info" className="mt-4">
                      <Alert.Heading>
                        <i className="bi bi-info-circle me-2"></i>
                        Hinweis zur Berechnung
                      </Alert.Heading>
                      <p className="mb-0">{details.calculation_explanation.note}</p>
                    </Alert>

                    <h5 className="mt-4 mb-3">Beispiel</h5>
                    <Card bg="light">
                      <Card.Body>
                        <p><strong>Szenario:</strong></p>
                        <ul>
                          <li>Check-Intervall: {details.endpoint.check_interval} Sekunden</li>
                          <li>Erfolgreiche Checks: {details.overall_statistics.successful_checks}</li>
                          <li>Fehlgeschlagene Checks: {details.overall_statistics.failed_checks}</li>
                        </ul>

                        <p><strong>Ergebnis:</strong></p>
                        <ul>
                          <li>Uptime: {details.overall_statistics.uptime_percentage}%</li>
                          <li>Online: {details.overall_statistics.total_online_formatted}</li>
                          <li>Offline: {details.overall_statistics.total_offline_formatted}</li>
                        </ul>
                      </Card.Body>
                    </Card>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Schließen
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EndpointDetailsModal;
